import { AssessmentFooter, AssessmentHeader, AssessmentHome } from "../assessment/views";
import {
  AvikarCart,
  AvikarFooter,
  AvikarHeader,
  AvikarTemplate,
  RetailCart,
  SecondRetailTemplate,
  SFCart,
  SunFlareTemplate,
  SunFlareTemplateFooter,
  SunFlareTemplateHeader,
  TealFlowTemplate,
  TealFlowTemplateFooter,
  TealFlowTemplateHeader,
} from "../catalog/views";
import EducationCart from "../../views/LandingPage/EducationDomain/EducationCart";
import EducationDomainTemplate from "../../views/LandingPage/EducationDomain/EducationDomainTemplate.jsx";
import { FlexCartNew, FlexOfficeMain } from "../flexOffice/views";
import {
  InstitutionCart,
  InstitutionDomainTemplate,
  InstitutionFooter,
  InstitutionHeader,
} from "../institution/views";
import { KrumagoFooter, KrumagoHeader, KrumagoParentMain } from "../krumago/views";
import { ServiceFooter, ServiceHeader, ServiceMain } from "../services/views";
import { ConfigurableEducationFooter, ConfigurableEducationHeader } from "template/EducationTemplateChrome.jsx";
import { ConfigurableFlexOfficeFooter, ConfigurableFlexOfficeHeader } from "template/FlexOfficeTemplateChrome.jsx";
import { ConfigurableRetailFooter, ConfigurableRetailHeader } from "template/RetailTemplateChrome.jsx";
import { ConfigurableWaasFooter, ConfigurableWaasHeader, WaasFallbackHome } from "template/WaasTemplateChrome.jsx";
import { educationNavList, institutionNavList, retailNavList } from "./domainNavigation.jsx";
import { getDomainColors, getDomainLabels } from "./domainPresentation";
import { DOMAIN_TYPES } from "./domainTypes";

const getCurrentTemplate = (runtimeContext = {}) => {
  const activeTemplate = runtimeContext.activeTemplate;
  // `templateKey` is the unique persisted instance key (for example
  // `RETAIL_123_...`). Retail chrome selection is based on the chosen family
  // style, which is stored in `baseTemplateKey`/`sourceTemplate` as
  // `AvikarTemplate`, `SunFlareTemplate`, or `TealFlowTemplate`.
  const contextTemplateKey = activeTemplate?.baseTemplateKey
    || activeTemplate?.attributes?.sourceTemplate
    || activeTemplate?.attributes?.retailTemplateStyle
    || activeTemplate?.templateKey
    || activeTemplate?.attributes?.templateKey;
  if (contextTemplateKey) return contextTemplateKey;

  const contextAttributes = runtimeContext.company?.dynamicAttributes;
  if (Array.isArray(contextAttributes)) {
    const contextTemplateAttribute = contextAttributes.find((attr) => (
      String(attr?.name || "").toLowerCase().startsWith("template")
    ));
    if (contextTemplateAttribute?.value) return contextTemplateAttribute.value;
  }

  return null;
};

export const DOMAIN_CONFIG = {
  [DOMAIN_TYPES.RETAIL]: {
    navList: retailNavList,
    colors: getDomainColors(DOMAIN_TYPES.RETAIL),
    labels: getDomainLabels(DOMAIN_TYPES.RETAIL)
  },
  [DOMAIN_TYPES.EDUCATION]: {
    navList: educationNavList,
    colors: getDomainColors(DOMAIN_TYPES.EDUCATION),
    labels: getDomainLabels(DOMAIN_TYPES.EDUCATION)
  },
  [DOMAIN_TYPES.INSTITUTION]: {
    navList: institutionNavList,
    colors: getDomainColors(DOMAIN_TYPES.INSTITUTION),
    labels: getDomainLabels(DOMAIN_TYPES.INSTITUTION)
  },
  [DOMAIN_TYPES.FLEX_OFFICE]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.FLEX_OFFICE),
    labels: getDomainLabels(DOMAIN_TYPES.FLEX_OFFICE)
  },
  [DOMAIN_TYPES.ASSESSMENT]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.ASSESSMENT),
    labels: getDomainLabels(DOMAIN_TYPES.ASSESSMENT)
  },
  [DOMAIN_TYPES.SERVICES]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.SERVICES),
    labels: getDomainLabels(DOMAIN_TYPES.SERVICES)
  },
  [DOMAIN_TYPES.WAAS]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.WAAS),
    labels: getDomainLabels(DOMAIN_TYPES.WAAS)
  },
  [DOMAIN_TYPES.KRUMAGO]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.KRUMAGO),
    labels: getDomainLabels(DOMAIN_TYPES.KRUMAGO)
  },
  [DOMAIN_TYPES.DEFAULT]: {
    navList: null,
    colors: getDomainColors(DOMAIN_TYPES.DEFAULT),
    labels: getDomainLabels(DOMAIN_TYPES.DEFAULT)
  }
};

export const getDomainConfig = (domainType, runtimeContext = {}) => {
  const template = getCurrentTemplate(runtimeContext);

  const baseConfigs = {
    [DOMAIN_TYPES.ASSESSMENT]: {
      header: AssessmentHeader,
      footer: AssessmentFooter,
      pageTemplate: AssessmentHome,
      cartTemplate: null,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.ASSESSMENT),
      labels: getDomainLabels(DOMAIN_TYPES.ASSESSMENT)
    },
    [DOMAIN_TYPES.INSTITUTION]: {
      header: InstitutionHeader,
      footer: InstitutionFooter,
      pageTemplate: InstitutionDomainTemplate,
      cartTemplate: InstitutionCart,
      userRole: null,
      navList: institutionNavList,
      colors: getDomainColors(DOMAIN_TYPES.INSTITUTION),
      labels: getDomainLabels(DOMAIN_TYPES.INSTITUTION)
    },
    [DOMAIN_TYPES.FLEX_OFFICE]: {
      header: ConfigurableFlexOfficeHeader,
      footer: ConfigurableFlexOfficeFooter,
      pageTemplate: FlexOfficeMain,
      cartTemplate: FlexCartNew,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.FLEX_OFFICE),
      labels: getDomainLabels(DOMAIN_TYPES.FLEX_OFFICE)
    },
    [DOMAIN_TYPES.SERVICES]: {
      header: ServiceHeader,
      footer: ServiceFooter,
      pageTemplate: ServiceMain,
      cartTemplate: null,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.SERVICES),
      labels: getDomainLabels(DOMAIN_TYPES.SERVICES)
    },
    [DOMAIN_TYPES.WAAS]: {
      header: ConfigurableWaasHeader,
      footer: ConfigurableWaasFooter,
      pageTemplate: WaasFallbackHome,
      cartTemplate: null,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.WAAS),
      labels: getDomainLabels(DOMAIN_TYPES.WAAS)
    },
    [DOMAIN_TYPES.DEFAULT]: {
      header: null,
      footer: null,
      pageTemplate: null,
      cartTemplate: null,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.DEFAULT),
      labels: getDomainLabels(DOMAIN_TYPES.DEFAULT)
    },
    [DOMAIN_TYPES.KRUMAGO]: {
      header: KrumagoHeader,
      footer: KrumagoFooter,
      pageTemplate: KrumagoParentMain,
      cartTemplate: null,
      userRole: null,
      navList: null,
      colors: getDomainColors(DOMAIN_TYPES.KRUMAGO),
      labels: getDomainLabels(DOMAIN_TYPES.KRUMAGO)
    }
  };

  if (domainType === DOMAIN_TYPES.RETAIL) {
    return {
      header: template === "TealFlowTemplate" ? TealFlowTemplateHeader : template === "SunFlareTemplate" ? SunFlareTemplateHeader : template === "AvikarTemplate" ? AvikarHeader : ConfigurableRetailHeader,
      footer: template === "TealFlowTemplate" ? TealFlowTemplateFooter : template === "SunFlareTemplate" ? SunFlareTemplateFooter : template === "AvikarTemplate" ? AvikarFooter : ConfigurableRetailFooter,
      pageTemplate: template === "TealFlowTemplate" ? TealFlowTemplate : template === "SunFlareTemplate" ? SunFlareTemplate : template === "AvikarTemplate" ? AvikarTemplate : SecondRetailTemplate,
      cartTemplate: template === "AvikarTemplate" ? AvikarCart : template === "SunFlareTemplate" ? SFCart : RetailCart,
      userRole: "CUSTOMER",
      navList: retailNavList,
      colors: getDomainColors(DOMAIN_TYPES.RETAIL),
      labels: getDomainLabels(DOMAIN_TYPES.RETAIL)
    };
  }

  if (domainType === DOMAIN_TYPES.EDUCATION) {
    return {
      header: template === "TealFlowTemplate" ? TealFlowTemplateHeader : template === "SunFlareTemplate" ? SunFlareTemplateHeader : ConfigurableEducationHeader,
      footer: template === "TealFlowTemplate" ? TealFlowTemplateFooter : template === "SunFlareTemplate" ? SunFlareTemplateFooter : ConfigurableEducationFooter,
      pageTemplate: template === "TealFlowTemplate" ? TealFlowTemplate : template === "SunFlareTemplate" ? SunFlareTemplate : EducationDomainTemplate,
      cartTemplate: EducationCart,
      userRole: "STUDENT",
      navList: educationNavList,
      colors: getDomainColors(DOMAIN_TYPES.EDUCATION),
      labels: getDomainLabels(DOMAIN_TYPES.EDUCATION)
    };
  }

  return baseConfigs[domainType] || baseConfigs[DOMAIN_TYPES.DEFAULT];
};
